import { useDispatch, useSelector } from "react-redux";
import {
  changeGlobalState,
  changeErrorData,
  changeIndValidacion,
} from "@/redux/actions/global";
import HTTP_REQUEST from "@/utils/http";
import url from "@/utils/url";
import helpers from "@/utils/helpers";
import { API_CANAL, StateMachine } from "@/utils/consts";
import {
  ERROR_CODE_NOT_FOUND,
  LIMIT_YOUNGER_AGE,
  LIMIT_OLDER_AGE_PLUS,
  LIMIT_OLDER_AGE,
  LIMIT_OLDER_AGE_VIAJES,
  IDE_TERCERO,
  BDU_ORIGIN,
  AX_ORIGIN,
  TERCERO_ERROR,
} from "@/utils/consts";
import { selectDomicilio, cleanUbigeo } from "@/redux/actions/domcilio";
import {
  getPersonData,
  getPersonSuccess,
  getPersonDataFilled,
  cleanPerson,
  setIsNewClient,
} from "@/redux/actions/vida";
import { getCodeSecurity } from "@/utils/apiCanal";
import { decryptAES, encryptAES } from "@/utils/CryptographyUtils";
import { ADD_THIRD_SUCCESS } from "@/redux/types/plans";
import {
  isValidRoadType,
  isValidStreetName,
  isValidStreetNumber,
} from "@/utils/address-validation";
import { DateTime } from "luxon";
import { LIST_TYPE_ROAD } from "@/constants/list-type-roads";
import { getProductOptions } from "@/utils/product";

export const usePersonService = () => {
  const isVE = helpers.validateVidaEndosable();
  const { digitalDataProductName } = getProductOptions();
  const { beneficiaries } = useSelector((state) => state.beneficiaries);
  const code = isVE ? "ErrorVidaEndosable" : "code";

  const dispatch = useDispatch();

  const getPerson = async (data, cb) => {
    const isVidaplus = helpers.validateVidaPlus();
    const isViajes = helpers.validateViajes();
    data.canal = "vcd";
    const minAge = LIMIT_YOUNGER_AGE;
    const maxAge = isVidaplus ? LIMIT_OLDER_AGE_PLUS : isViajes ? LIMIT_OLDER_AGE_VIAJES : LIMIT_OLDER_AGE;
    try {
      dispatch(cleanPerson());
      const apiUrl = url.newObtenerTercero;
      const respTerceros = await HTTP_REQUEST.postWitchTrace(
        apiUrl,
        data,
        "APIS_VCT",
        API_CANAL
      );
      const {
        payload: payloadTerceros,
        status: statusTerceros,
      } = respTerceros.response;
      let terceroJSON = JSON.parse(
        decryptAES(payloadTerceros, getCodeSecurity())
      );
      window.originTJSON = terceroJSON?.origen;
      if (statusTerceros.success) {
        helpers.getTrackBDU(
          `Origen validación Cliente - ${digitalDataProductName}`,
          terceroJSON?.origen,
          {
            typeDoc: data.tipoDocumento,
            numDoc: data.numeroDocumento,
          }
        );
        if (
          terceroJSON.origen === BDU_ORIGIN ||
          terceroJSON.origen === AX_ORIGIN
        ) {
          const edad = helpers.calcularEdad(terceroJSON.fechaNacimiento);
          if (edad > maxAge || edad < minAge) {
            dispatch(changeGlobalState(6));
          } else {
            if (!data.withoutUbigeo) {
              const {
                departamento,
                provincia,
                distrito,
                nombreVia,
                tipoVia,
                numeroVia,
              } = helpers.getClientUbigeo(terceroJSON);
              const isValidUbigeo =
                departamento?.value &&
                provincia?.value &&
                distrito?.value &&
                isValidStreetName(nombreVia) &&
                isValidStreetNumber(numeroVia) &&
                isValidRoadType(tipoVia?.value);
              if (departamento) {
                const payloadDomicilio = {
                  validUbigeo: isValidUbigeo,
                  departamento,
                  provincia,
                  distrito,
                  nombreVia: nombreVia || "",
                  ...(tipoVia.text && { tipoVia }),
                  ...(numeroVia && { numeroVia }),
                };
                dispatch(selectDomicilio(payloadDomicilio));
              }
            }
            dispatch(changeGlobalState(0));
          }
          cb && cb(terceroJSON);
          dispatch(getPersonData(terceroJSON));
          dispatch(getPersonSuccess());
        } else {
          cb && cb(terceroJSON);
          dispatch(changeGlobalState(9));
          dispatch(setIsNewClient(true));
          dispatch(getPersonSuccess());
          return;
        }
      }
      return statusTerceros.success;
    } catch (error) {
      dispatch(cleanUbigeo());
      if (error.response !== undefined) {
        const errorCode = error.response.data?.response?.status?.error?.code;
        if (
          errorCode === TERCERO_ERROR.RENIEC_ERROR &&
          data.tipoDocumento === "4"
        ) {
          dispatch(changeGlobalState(9));
          dispatch(setIsNewClient(true));

          dispatch(getPersonSuccess());
          return;
        }
        if (
          errorCode === TERCERO_ERROR.INVALID_ERROR ||
          errorCode === TERCERO_ERROR.RENIEC_ERROR
        ) {
          helpers.getTrackBDU(`Origen validación Cliente - ${digitalDataProductName}`, "NE", {
            typeDoc: data.tipoDocumento,
            numDoc: data.numeroDocumento,
          });
          !data.isChildren && dispatch(changeGlobalState(14));
          dispatch(getPersonSuccess());
          dispatch(
            changeErrorData({
              error: error,
              code: ERROR_CODE_NOT_FOUND,
              message: error.message,
            })
          );
          helpers.getTagError(
            ERROR_CODE_NOT_FOUND,
            error.message,
            "trackError"
          );
          helpers.getTagMetadata(
            "Endpoint",
            url.obtenerTercero,
            "trackMetadata"
          );
        } else {
          dispatch(changeGlobalState(4));
          helpers.sendTagError(error, url.obtenerTercero);
        }
      } else {
        dispatch(changeGlobalState(4));
        helpers.sendTagError(error, url.obtenerTercero);
      }
    }
  };

  const getValidationsRules = async (data) => {
    try {
      const respValidacion = await HTTP_REQUEST.postWitchTrace(
        url.validationPoliza,
        { ...data, ideTercero: IDE_TERCERO },
        "APIS_VCT",
        API_CANAL
      );

      const { payload, status } = respValidacion.response;

      if (status.success) {
        const { indValidado, reglas = [] } = payload;

        dispatch(changeGlobalState(0));
        if (!indValidado) {
          const [
            blackList,
            yellowList,
            yellowListPEP,
            nroPolizas,
            cumulos,
          ] = reglas;
          if (data.onlyBL) {
            if (blackList && !blackList.valor) {
              dispatch(changeGlobalState(13));
              helpers.getTagError(code, StateMachine[13], "trackError");
              helpers.getTagMetadata(
                "Endpoint",
                url.validationPoliza,
                "trackMetadata"
              );
            }
            return;
          }
          if (blackList && !blackList.valor) {
            helpers.getTagError(code, StateMachine[13], "trackError");
            helpers.getTagMetadata(
              "Endpoint",
              url.validationPoliza,
              "trackMetadata"
            );
          }
          if (nroPolizas && !nroPolizas.valor) {
            helpers.getTagError(code, StateMachine[10], "trackError");
            helpers.getTagMetadata(
              "Endpoint",
              url.validationPoliza,
              "trackMetadata"
            );
          }
          if (
            (yellowList && !yellowList.valor) ||
            (yellowListPEP && !yellowListPEP.valor)
          ) {
            helpers.getTagError(code, StateMachine[11], "trackError");
            helpers.getTagMetadata(
              "Endpoint",
              url.validationPoliza,
              "trackMetadata"
            );
          }
          if (cumulos && !cumulos.valor) {
            helpers.getTagError(code, StateMachine[12], "trackError");
            helpers.getTagMetadata(
              "Endpoint",
              url.validationPoliza,
              "trackMetadata"
            );
          }
          const validationData = {
            indPoliza: helpers.validateVidaPlus()
              ? nroPolizas && !nroPolizas.valor
              : false,
            indYellowList:
              (yellowList && !yellowList.valor) ||
              (yellowListPEP && !yellowListPEP.valor),
            indCumulos: cumulos && !cumulos.valor,
            indBlackList: blackList && !blackList.valor,
          };
          dispatch(changeIndValidacion(validationData));
        }
        dispatch(getPersonDataFilled(indValidado));
        dispatch(getPersonSuccess());
      } else {
        dispatch(changeGlobalState(9));
        dispatch(setIsNewClient(true));
        dispatch(getPersonSuccess());
      }
      return status.success;
    } catch (error) {
      dispatch(changeGlobalState(4));
      helpers.sendTagError(error, url.validationPoliza);
    }
  };

  const validatePersonData = async (request) => {
    try {
      request.canal = "vcd";
      const { response } = await HTTP_REQUEST.postWitchTrace(
        url.validarTercero,
        request,
        "APIS_VCT",
        API_CANAL
      );
      const { payload, status } = response;
      let payloadJSON = JSON.parse(
        decryptAES(payload, getCodeSecurity())
      );
      if(status.success) {
        const { resultinterpretacion, resultvalidacion: { resultinputref, resultinputper } } = payloadJSON;
        return { resultinterpretacion, resultinputref, resultinputper };
      } else {
        dispatch(changeGlobalState(14));
      }
    } catch (error) {
      dispatch(changeGlobalState(14));
      helpers.sendTagError(error, url.validarTercero);
    }
  };

  const addThirdToQuote = async (userData, domicilio, plans) => {
    try {
      const user = {
        codTercero:
          userData.tercero === "null" || !userData.tercero
            ? undefined
            : Number(userData.tercero),
        tipoDocumento: Number(userData.tipoDocumento || userData.typeDoc),
        numeroDocumento: userData.numeroDocumento || userData.documento,
        nombre: (userData.nombres ?? "").trim(),
        apePaterno: (userData.apellidoPaterno ?? "").trim(),
        apeMaterno: (userData.apellidoMaterno ?? "").trim(),
        sexo: userData.sexo,
        fecNacimiento: userData.fechaNacimiento || userData.fecNac,
        ubigeo: domicilio.ubigeo,
        tipoVia: domicilio.tipoVia.id,
        nombreVia: (domicilio.nombreVia ?? "").trim(),
        numeroVia: domicilio.numeroVia,
        correo: userData.correo,
        telefono: userData.telefono,
        proteccionDatosPersonales: "S",
        envioComunicacionesComerciales: "S",
        porcentajeParticipacion: 100,
      };

      const data = {
        ideTransaccion: plans.ideTransaccion,
        terceros: encryptAES(
          JSON.stringify([
            { ...user, ideRol: 8 },
            { ...user, ideRol: 23 },
          ]),
          getCodeSecurity()
        ),
        asegurados: encryptAES(
          JSON.stringify([
            {
              ...user,
              ideRol: 9,
              ideTipoRelTer: 134,
              ideAsegurado: 0,
              beneficiarios: parseDataBeneficiaries() || [],
            },
          ]),
          getCodeSecurity()
        ),
        encriptado: true,
      };

      const { response } = await HTTP_REQUEST.postWitchTrace(
        url.agregarTercero,
        data,
        "APIS_VCT",
        API_CANAL
      );

      if (response.status) {
        dispatch({ type: ADD_THIRD_SUCCESS });
        return response;
      } else {
        dispatch(changeGlobalState(4));
      }
    } catch (error) {
      dispatch(changeGlobalState(4));
      helpers.sendTagError(error, url.beneficiaries);
    }
  };

  const parseDataBeneficiaries = () => {
    const beneficiariesData = beneficiaries.map((beneficiary) => {
      const {
        names,
        lastName,
        surName,
        documentNumber,
        documentType,
        birthdate,
        gender,
        email,
        phone,
        percentage,
        relationship,
        department,
        province,
        district,
        streetType,
        address,
        streetNumber,
      } = beneficiary;

      const departmentId = department.substring(1);
      const provinceid = province.substring(1);
      const districtId = district.substring(1);
      const ubigeo = `${departmentId}${provinceid}${districtId}`;
      const currentStreetType = LIST_TYPE_ROAD.find(
        (item) => item.value === streetType
      );

      return {
        nombre: names.toUpperCase(),
        porcentajeParticipacion: Number(percentage),
        parentesco: Number(relationship),
        numeroDocumento: documentNumber,
        apePaterno: lastName.toUpperCase(),
        apeMaterno: String(surName || "").toUpperCase(),
        sexo: gender,
        correo: email,
        telefono: phone,
        fecNacimiento: DateTime.fromFormat(birthdate, "dd/MM/yyyy").toFormat(
          "yyyy-MM-dd"
        ),
        tipoDocumento: Number(documentType),
        nombreCompleto: `${names.toUpperCase()} ${lastName.toUpperCase()} ${surName.toUpperCase()}`,
        proteccionDatosPersonales: "S",
        envioComunicacionesComerciales: "N",
        ubigeo,
        tipoVia: currentStreetType?.id,
        nombreVia: String(address).toUpperCase(),
        numeroVia: String(streetNumber) || "SN",
      };
    });

    return beneficiariesData;
  };

  return {
    getPerson,
    getValidationsRules,
    validatePersonData,
    addThirdToQuote,
  };
};
